import type { TabId } from "@/api/types";

export interface NavItem {
  label: string;
  path: string;
  icon?: string;
}

export interface AppConfig {
  id: string;
  name: string;
  shortName: string;
  basePath: string;          // relative to the tab's basePath (e.g. "sites")
  description: string;
  icon: string;
  navItems: NavItem[];
  defaultRoute: string;
  /** Filtered out of the sidebar unless ``is_clinops_admin`` is true on
   * the user's permissions response. Routes still mount — non-admins
   * can't reach them without the link or a manual URL. */
  adminOnly?: boolean;
}

export interface TabConfig {
  id: TabId;
  label: string;
  basePath: string;          // absolute, e.g. "/clinops"
  apps: AppConfig[];
}

export const TAB_REGISTRY: TabConfig[] = [
  {
    id: "clinops",
    label: "ClinOps",
    basePath: "/clinops",
    apps: [
      {
        id: "sites",
        name: "Sites",
        shortName: "Sites",
        basePath: "sites",
        description: "Health systems in the onboarding pipeline",
        icon: "Building2",
        defaultRoute: "",
        navItems: [
          { label: "Overview", path: "", icon: "LayoutDashboard" },
          { label: "New site", path: "new", icon: "PlusCircle" },
          { label: "Organizations", path: "organizations", icon: "Building" },
          { label: "Studies", path: "studies", icon: "Microscope" },
        ],
      },
      {
        id: "documents",
        name: "Documents",
        shortName: "Documents",
        basePath: "documents",
        description: "Document repository",
        icon: "Document",
        defaultRoute: "",
        navItems: [],
      },
      {
        id: "worklist",
        name: "Worklist",
        shortName: "Worklist",
        basePath: "worklist",
        description: "Open action items, risks, and upcoming dates across all sites",
        icon: "Worklist",
        defaultRoute: "",
        navItems: [],
      },
      {
        id: "audit",
        name: "Audit log",
        shortName: "Audit",
        basePath: "audit",
        description: "Append-only activity log across all sites; intended for admin review",
        icon: "Worklist",
        defaultRoute: "",
        navItems: [],
      },
      {
        id: "administration",
        name: "Administration",
        shortName: "Admin",
        basePath: "administration",
        description: "ClinOps configuration — therapeutic areas, document requirements, milestones",
        icon: "Building",
        defaultRoute: "",
        navItems: [
          { label: "Therapeutic areas", path: "therapeutic-areas", icon: "Stethoscope" },
          { label: "Document requirements", path: "document-requirements", icon: "Document" },
          { label: "Milestones", path: "milestones", icon: "Microscope" },
        ],
        adminOnly: true,
      },
    ],
  },
  {
    id: "dashboard",
    label: "Dashboard",
    basePath: "/dashboard",
    apps: [],
  },
];

/** Absolute path for an app within a tab — joins tab basePath and app basePath. */
export function appPath(tab: TabConfig, app: AppConfig): string {
  return app.basePath ? `${tab.basePath}/${app.basePath}` : tab.basePath;
}
